// @safetyhub/report-composer — 프리미티브 컴포넌트 (6종)
//
// 각 컴포넌트는 { node, value } props를 받는다.
// value = 이미 resolve된 바인딩 값(Phase 1에서는 RenderData에서 직접 추출).
// 모든 root에 nodeLayoutToStyle(node.layout)을 병합한다.
//
// 모든 프리미티브의 최외곽 엘리먼트는 `data-composer-node-id={node.id}`를 방출한다.
// 웹 렌더 동작에는 무영향(속성만 추가)이며, Composer Studio(admin)가 클릭 선택 시
// `closest('[data-composer-node-id]')`로 노드 id를 역추적하는 데 쓴다.

import { isValidElement } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import type {
  RectNode,
  TextNode,
  ImageNode,
  LineNode,
  SpacerNode,
} from '@safetyhub/shared';
import {
  nodeLayoutToStyle,
  applyRectDecoration,
  alignToFlex,
  vAlignToTableCss,
  contentHToTextAlign,
  textStyleToCss,
  type ParentContext,
} from './layout';
import { BINDING_PLACEHOLDER_COLOR } from './placeholder';
import { FitPlaceholder } from './FitPlaceholder';
import { AnnotatedImageView, isAnnotatedImageValue } from './AnnotatedImageView';
import { ImageWithFallback } from './ImageWithFallback';
// 기간(date-range) 값은 객체 — 텍스트 노드가 String()으로 접기 전에 갈라낸다(셀 경로와 같은 규약).
import { dateRangeRawText, isDateRangeValue } from './dateValue';

// ── Rect ─────────────────────────────────────────────
export function RectPrim({ node, parent }: { node: RectNode; value?: unknown; parent?: ParentContext }) {
  const style: CSSProperties = { ...nodeLayoutToStyle(node.layout, parent) };
  applyRectDecoration(style, node.style);
  return <div data-composer-node-id={node.id} style={style} />;
}

// ── Text ─────────────────────────────────────────────
export function TextPrim({
  node,
  value,
  parent,
  placeholder,
  placeholderColor,
  placeholderTitle,
  provenance,
}: {
  node: TextNode;
  value?: unknown;
  parent?: ParentContext;
  /** admin 편집/미리보기: 정적 text·실값이 모두 빈 경우 표시할 회색 {label} placeholder. */
  placeholder?: string;
  /** placeholder 톤 override('unresolvable-only' 모드의 amber). 미지정=기존 회색(회귀 0). */
  placeholderColor?: string;
  /** placeholder 안내 툴팁('unresolvable-only' 모드). 미지정=기존 라벨 title(회귀 0). */
  placeholderTitle?: string;
  /**
   * admin 미리보기: **실값이 그려질 때** 그 값의 출처(모사/연결 보고서)를 알리는 점선 밑줄+툴팁.
   * 미지정=아무 장식 없음(회귀 0). 정적 text가 이긴 경우는 바인딩 값이 화면에 없으므로 미적용.
   */
  provenance?: { style: CSSProperties; title: string };
}) {
  // 공통 텍스트-런 스타일(폰트/색/자간/줄간격)은 textStyleToCss로 일원화. textIndent와 레이아웃
  // 정렬(contentAlignH/V)·박스 장식은 TextPrim 고유라 별도 부여.
  const style: CSSProperties = { ...nodeLayoutToStyle(node.layout, parent), ...textStyleToCss(node.style) };
  const cH = node.layout?.contentAlignH;
  if (cH) style.textAlign = contentHToTextAlign(cH);
  const cV = node.layout?.contentAlignV;
  if (cV) {
    style.display = 'flex';
    style.flexDirection = 'column';
    style.justifyContent = alignToFlex(cV);
  }
  if (node.style?.textIndent !== undefined) style.textIndent = `${node.style.textIndent}mm`;
  // 박스 장식(채움/테두리/모서리) — "사각형+텍스트" 조합을 텍스트 하나로 대체. 미설정=아무것도 안 붙음(회귀 0).
  applyRectDecoration(style, node.boxStyle);
  // 입력한 줄바꿈/공백을 보존(기본 white-space:normal은 개행을 공백으로 접음). 긴 줄은 래핑.
  style.whiteSpace = 'pre-wrap';
  // 실값 우선: 정적 node.text(비어있지 않으면) 또는 resolve된 value(빈값 아님)를 표시.
  // ⚠️ node.text가 빈 문자열('')이면 "정적 텍스트 없음"으로 간주한다 — 바인딩 텍스트 노드는
  //    저작 시 text:''로 만들어지는데, `'' ?? value`는 왼쪽(빈 문자열)을 유지해 바인딩 value를
  //    덮어버려(값이 있어도 미표시) improvement-opinion 등이 미리보기/출력에서 빈칸이 됐다.
  // 둘 다 빈데 placeholder가 주어지면(admin 편집/미리보기) 회색 {label} placeholder를 표시한다.
  const staticText = node.text !== undefined && node.text !== '' ? node.text : undefined;
  // 값이 React 엘리먼트면(render 지점이 만든 비주얼 — rangeMap 배지 등) 문자열화하지 않고 그대로
  // 그린다. 정적 text 우선 규칙은 그대로라 staticText가 있으면 종전 문자열 경로다. 이 분기를 두는
  // 이유는 배지가 노드의 폰트/색/정렬(위 style)을 그대로 물려받아 텍스트일 때와 자리가 같아지게
  // 하려는 것. (value는 unknown이라 @types/react 18 시그니처에 맞춰 캐스트 후 판정한다.)
  const elementValue = isValidElement(value as object | null | undefined)
    ? (value as ReactElement)
    : undefined;
  // 출처 표식은 **바인딩 값이 실제로 화면에 나올 때만** 붙인다 — 정적 text가 이긴 셀/노드는
  // 그 값이 안 보이므로 밑줄이 거짓말이 된다.
  const provenanceStyle = staticText === undefined ? provenance?.style : undefined;
  const provenanceTitle = staticText === undefined ? provenance?.title : undefined;
  if (staticText === undefined && elementValue !== undefined) {
    // ⚠️ contentAlignV가 지정되면 위에서 display:flex(column)가 붙는다 — 그 안의 배지 <span>은
    // flex item이라 기본 align-items:stretch로 **셀 폭 전체까지 늘어난다**(알약 배경이 퍼진다).
    // 가로 정렬을 textAlign과 같은 뜻으로 맞춰 알약이 제 폭만 갖게 한다(텍스트일 때와 같은 자리).
    const badgeStyle: CSSProperties =
      style.display === 'flex' ? { ...style, alignItems: alignToFlex(cH ?? 'start') } : style;
    return (
      <div
        data-composer-node-id={node.id}
        style={provenanceStyle ? { ...badgeStyle, ...provenanceStyle } : badgeStyle}
        title={provenanceTitle}
      >
        {elementValue}
      </div>
    );
  }
  // 기간(date-range) 값은 객체라 String()을 타면 '[object Object]'가 본문에 찍힌다 — 표 셀 경로와
  // 같은 규약으로 raw 표기("2026-08-28 ~ 2026-08-30")로 접는다(형식 지정 시엔 이미 문자열로 온다).
  // 양끝이 다 빈 기간은 빈값과 같이 취급해 placeholder 경로로 내려보낸다.
  const rangeText = isDateRangeValue(value) ? dateRangeRawText(value) : undefined;
  const textValue = rangeText !== undefined ? (rangeText === '' ? '' : rangeText) : value;
  const resolved =
    staticText ??
    (textValue === null || textValue === undefined || textValue === ''
      ? undefined
      : String(textValue));
  if (resolved === undefined && placeholder !== undefined) {
    return (
      <div
        data-composer-node-id={node.id}
        style={{ ...style, color: placeholderColor ?? BINDING_PLACEHOLDER_COLOR }}
      >
        <FitPlaceholder
          text={placeholder}
          title={placeholderTitle ? `${placeholder} — ${placeholderTitle}` : undefined}
        />
      </div>
    );
  }
  const content = resolved ?? '';
  // 값이 실제로 있을 때만 출처 밑줄(빈 문자열엔 밑줄만 남아 뜻이 없다).
  const valueStyle = provenanceStyle && resolved !== undefined ? { ...style, ...provenanceStyle } : style;
  return (
    <div
      data-composer-node-id={node.id}
      style={valueStyle}
      title={resolved !== undefined ? provenanceTitle : undefined}
    >
      {content}
    </div>
  );
}

// ── Image ────────────────────────────────────────────
export function ImagePrim({ node, value, parent }: { node: ImageNode; value?: unknown; parent?: ParentContext }) {
  // 값이 주석 이미지 객체({ url, shapes?, strokes? })면 오버레이 렌더로 분기한다.
  // 위젯 축(widget:'annotated-image')과 대칭 규약: 객체=오버레이 / 문자열=평면(하위호환).
  // body는 노드 kind가 곧 image 하나뿐이라 별도 kind를 늘리지 않고 값 형태로 분기한다
  // (객체 값은 이전까지 String(value)='[object Object]'가 src로 나가 깨진 이미지였으므로 회귀 0).
  if (isAnnotatedImageValue(value)) {
    // ⚠️ 오버레이는 0~1 정규화 좌표라 **원본 비율 전체 표시**가 전제다(AnnotatedImageView는
    // width:100% + height:auto). 그래서 이 분기에서는:
    //   - node.fit(cover)을 적용하지 않는다 — 잘라 채우면 좌표가 가리키는 자리가 사라진다.
    //   - 고정/채움 height와 minHeight를 벗긴다 — 비율대로 자란 이미지가 그 박스를 넘쳐 흘러
    //     인접 노드를 덮는 것을 막는다(폭이 표시 배율을 정한다).
    //   - 단 **숫자 height는 상한(비율 유지 축소)으로 재해석**한다 — 고정 박스가 아니라
    //     AnnotatedImageView maxHeight(shrink-wrap — 좌표 정합 유지)로 전달. 별도 신규 축 없이
    //     에디터의 기존 높이 필드가 곧 상한 조작이 된다(2026-08-06 사용자 확정. 'fill'/백분율/
    //     미지정은 상한 없음 — 전폭 원본비율 그대로).
    // 나머지 레이아웃(폭/마진/패딩/flex)은 그대로 유지해 저작한 자리·크기를 지킨다.
    const wrapStyle: CSSProperties = { ...nodeLayoutToStyle(node.layout, parent) };
    delete wrapStyle.height;
    delete wrapStyle.minHeight;
    // 오버레이 스타일 옵션(node.overlay)은 저작 축 — 표시/숨김 판정은 데이터 접근이 필요해
    // RenderTemplate(case 'image')이 담당하고(숨김이면 shapes/strokes를 벗겨 넘김),
    // 여기서는 스타일 축(두께/색/라벨)만 내려준다. 전부 미지정이면 기존 렌더와 완전 동일.
    return (
      <div data-composer-node-id={node.id} style={wrapStyle}>
        <AnnotatedImageView
          value={value}
          strokeWidth={node.overlay?.strokeWidth}
          colorOverride={node.overlay?.color}
          showLabels={node.overlay?.showLabels}
          lineStyle={node.overlay?.lineStyle}
          showFill={node.overlay?.showFill}
          maxHeight={typeof node.layout?.height === 'number' ? node.layout.height : undefined}
        />
      </div>
    );
  }
  const style: CSSProperties = { ...nodeLayoutToStyle(node.layout, parent) };
  if (node.fit !== undefined) style.objectFit = node.fit;
  const cH = node.layout?.contentAlignH;
  const cV = node.layout?.contentAlignV;
  if (cH || cV) {
    style.objectPosition = `${cH ? contentHToTextAlign(cH) : 'center'} ${cV ? vAlignToTableCss(cV) : 'center'}`;
  }
  // 로드 실패는 깨진 아이콘이 아니라 같은 발자국의 안내 칸으로 접는다(ImageWithFallback 헤더 — P6-d).
  // `data-composer-node-id`는 폴백 상태에서도 실린다(admin 캔버스 클릭 선택 역추적).
  return (
    <ImageWithFallback
      data-composer-node-id={node.id}
      src={String(value ?? '')}
      style={style}
      alt=""
    />
  );
}

// ── Line ─────────────────────────────────────────────
export function LinePrim({ node, parent }: { node: LineNode; value?: unknown; parent?: ParentContext }) {
  const style: CSSProperties = { ...nodeLayoutToStyle(node.layout, parent) };
  if (node.orient === 'h') {
    style.borderTop = '1px solid currentColor';
  } else {
    style.borderLeft = '1px solid currentColor';
  }
  return <div data-composer-node-id={node.id} style={style} />;
}

// Table은 셀 content 재귀(renderNode) 때문에 RenderTemplate.tsx에서 렌더한다(순환 import 회피).

// ── Spacer ───────────────────────────────────────────
export function SpacerPrim({ node, parent }: { node: SpacerNode; value?: unknown; parent?: ParentContext }) {
  return <div data-composer-node-id={node.id} style={nodeLayoutToStyle(node.layout, parent)} />;
}
